import type {
  AdminInspectorDetailDto,
  AdminInspectorListItemDto,
  AdminInspectorSettableStatus,
  AdminInspectorStatus,
  ListAdminInspectorsResultDto,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin inspector-management surface (AUT-125 /
 * ADMIN-13), targeting the AUT-114 backend:
 *
 *   - `GET   /admin/inspectors`              (cursor-paginated)
 *   - `GET   /admin/inspectors/:id`
 *   - `POST  /admin/inspectors/:id/kyc/approve`
 *   - `PATCH /admin/inspectors/:id/status`
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list   → `['admin', 'inspectors', 'list', filters]`
 *   detail → `['admin', 'inspectors', 'detail', id]`
 * Mutations invalidate by the `['admin', 'inspectors']` prefix on success.
 *
 * Mirrors `admin-users.ts` + `admin-orders.ts`. Two deliberate divergences:
 *
 * 1. KYC approve is a `POST` with NO body (the session id is resolved on
 *    the server). It returns the freshly-projected `AdminInspectorDetailDto`
 *    — the caller patches the detail cache in place so the drawer doesn't
 *    flicker the "Одобрить KYC" footer button while the refetch is in
 *    flight (architect 🟠 #5). The list invalidates via the prefix.
 *
 * 2. Status PATCH returns the audit envelope `{dto: AdminInspectorListItemDto,
 *    previousStatus}`. The list-shape dto can't cross-patch detail — caller
 *    only invalidates the prefix (mirrors the orders force-cancel pattern).
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const INSPECTORS_BASE = '/admin/inspectors';

/** Stable query-key prefix; mutations invalidate against this. */
export const ADMIN_INSPECTORS_KEY = ['admin', 'inspectors'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminInspectorsQuerySchema` — `status` is a multi-select; `q` is
 * opaque free-text; `cityCountry` + `city` (key) come from the place dictionary
 * (the SPA enforces `MIN_SEARCH_LENGTH` on `q`
 * before it reaches here). `cursor` / `limit` drive cursor pagination.
 */
export interface AdminInspectorsListParams {
  status?: AdminInspectorStatus[];
  cityCountry?: string;
  city?: string;
  q?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminInspectorsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.cityCountry) query.cityCountry = params.cityCountry;
  if (params.city && params.city.length > 0) query.city = params.city;
  if (params.q && params.q.length > 0) query.q = params.q;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminInspectorsList = (
  params: AdminInspectorsListParams,
): UseQueryResult<ListAdminInspectorsResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_INSPECTORS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminInspectorsResultDto>(INSPECTORS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminInspectorDetail = (
  id: string | null,
): UseQueryResult<AdminInspectorDetailDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_INSPECTORS_KEY, 'detail', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminInspectorDetailDto>(`${INSPECTORS_BASE}/${id}`, {
        signal,
      });
      return data;
    },
  });

/**
 * Low-level KYC manual-approve. Role + permission + audit gated server-side
 * (AUT-138 removed per-action TOTP step-up).
 *
 * Empty request body — the session id to approve is resolved server-side.
 * Returns the freshly-projected `AdminInspectorDetailDto` so the caller
 * can patch the detail cache in place (architect 🟠 #5).
 */
export const postApproveKyc = async (id: string): Promise<AdminInspectorDetailDto> => {
  const { data } = await apiClient.post<AdminInspectorDetailDto>(
    `${INSPECTORS_BASE}/${id}/kyc/approve`,
    {},
  );
  return data;
};

/** Envelope returned by `PATCH /admin/inspectors/:id/status` (read `.dto`). */
interface PatchInspectorStatusResponse {
  dto: AdminInspectorListItemDto;
  previousStatus: string;
}

export interface PatchInspectorStatusBody {
  status: AdminInspectorSettableStatus;
  reason: string;
}

/**
 * Low-level status mutation. Role + permission + audit gated server-side
 * (AUT-138 removed per-action TOTP step-up).
 *
 * The `{dto, previousStatus}` envelope is read for the success path —
 * `dto` is the list-row shape, not the detail; caller invalidates the
 * `['admin','inspectors']` prefix instead of patching detail in place.
 */
export const patchInspectorStatus = async (
  id: string,
  body: PatchInspectorStatusBody,
): Promise<AdminInspectorListItemDto> => {
  const { data } = await apiClient.patch<PatchInspectorStatusResponse>(
    `${INSPECTORS_BASE}/${id}/status`,
    body,
  );
  return data.dto;
};

/**
 * Cache-helper exposed for the KYC approve + status hooks. `patchDetail`
 * lets the KYC approve write the response back into the detail cache so
 * the drawer doesn't flicker the "Одобрить KYC" footer during the refetch
 * (architect 🟠 #5); `invalidate` is the catch-all the caller runs to
 * refetch the list + drawer.
 */
export const useAdminInspectorsCache = (): {
  patchDetail: (id: string, detail: AdminInspectorDetailDto) => void;
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    patchDetail: (id, detail) => {
      queryClient.setQueryData([...ADMIN_INSPECTORS_KEY, 'detail', id], detail);
    },
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_INSPECTORS_KEY }),
  };
};
